"use client"

// Multi-select product picker with a families-mode footer. Families are
// rule-derived groups of products defined centrally on the Product Families page
// (?page=shopify-bundle-rules) — this picker only loads them. The
// `onSetNameChange` callback is invoked with the family's display name
// when a family is loaded (Bundle Builder uses it to label slots).

import { useCallback, useEffect, useState } from "react"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Loader2, X, Search, ChevronDown, Info, Layers, Settings } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { useProductFamilies } from "@/hooks/use-product-families"
import { cn } from "@/lib/utils"
import type { ProductFamilyLight, ProductOption } from "@/types/shopify"
import { productSubtitle } from "@/types/shopify"
import { useTranslations } from "next-intl"



const MIN_SEARCH_LENGTH = 3
const SELECT_ALL_LIMIT = 100
/** Show collapsed family badge when this many or more are selected */
const COLLAPSE_THRESHOLD = 2

interface ProductMultiSelectProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal: number
  /** Currently selected products */
  selected: ProductOption[]
  /** Max number of selectable products */
  maxSelect?: number
  onSelectionChange: (products: ProductOption[]) => void
  placeholder?: string
  /**
   * Enable product family load entries in the dropdown. Defaults to true
   * when maxSelect > 1.
   */
  enableFamilies?: boolean
  /**
   * Called when the active family-loaded label changes (family loaded /
   * cleared by mutating selection). Bundle Builder uses this to label slots.
   */
  onSetNameChange?: (name: string | null) => void
}

export function ProductMultiSelect({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal,
  selected,
  maxSelect = 1,
  onSelectionChange,
  placeholder = "Search products...",
  enableFamilies,
  onSetNameChange,
}: ProductMultiSelectProps) {

  const tUi = useTranslations("Shopify.ShopifyCrossSellProductMultiSelect")

  const { fetchWithAuth } = useAuth()
  const [search, setSearch] = useState("")
  const [results, setResults] = useState<ProductOption[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  /** Display name of the family currently loaded into selection (read-only). */
  const [activeFamilyName, setActiveFamilyName] = useState<string | null>(null)

  const showFamilies = enableFamilies ?? maxSelect > 1
  const { families, isLoading: isLoadingFamilies, hydrateFamily } = useProductFamilies({
    integrationId,
    period: { monthStartUtc, monthEndUtc, minimumOrderTotal },
    enabled: showFamilies,
  })
  const [pendingFamilyId, setPendingFamilyId] = useState<string | null>(null)

  const selectedIds = new Set(selected.map((p) => p.product_id))

  const doSearch = useCallback(
    async (query: string) => {
      if (query.length < MIN_SEARCH_LENGTH || !integrationId) {
        setResults([])
        return
      }

      setIsSearching(true)
      try {
        const params = new URLSearchParams({
          integration_id: integrationId,
          search: query,
          month_start_utc: monthStartUtc,
          month_end_utc: monthEndUtc,
          minimum_order_total: String(minimumOrderTotal),
        })

        const res = await fetchWithAuth(`/api/integrations/shopify/products?${params}`)
        if (res.ok) {
          const data = await res.json()
          setResults(data.products ?? [])
        }
      } catch {
        setResults([])
      } finally {
        setIsSearching(false)
      }
    },
    [integrationId, monthStartUtc, monthEndUtc, minimumOrderTotal, fetchWithAuth],
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      void doSearch(search)
    }, 300)
    return () => clearTimeout(timer)
  }, [search, doSearch])

  const availableResults = results.filter((p) => !selectedIds.has(p.product_id))
  const remaining = maxSelect - selected.length
  const canAddMore = remaining > 0

  const clearFamilyTracking = () => {
    setActiveFamilyName(null)
    onSetNameChange?.(null)
  }

  const handleToggle = (product: ProductOption) => {
    if (selectedIds.has(product.product_id)) {
      onSelectionChange(selected.filter((p) => p.product_id !== product.product_id))
      clearFamilyTracking()
    } else {
      if (!canAddMore) return
      const next = [...selected, product]
      onSelectionChange(next)
      // Adding products preserves family tracking — only removal clears it
      if (next.length >= maxSelect) {
        setShowDropdown(false)
        setSearch("")
      }
    }
  }

  const selectAllCount = Math.min(availableResults.length, remaining, SELECT_ALL_LIMIT)

  const handleSelectAll = () => {
    if (!canAddMore || availableResults.length === 0) return
    const toAdd = availableResults.slice(0, selectAllCount)
    onSelectionChange([...selected, ...toAdd])
    // Adding products preserves family tracking
    setShowDropdown(false)
    setSearch("")
  }

  const handleRemove = (productId: number) => {
    onSelectionChange(selected.filter((p) => p.product_id !== productId))
    clearFamilyTracking()
  }

  const handleSelectFamily = async (family: ProductFamilyLight) => {
    if (pendingFamilyId) return
    setPendingFamilyId(family.id)
    try {
      const hydrated = await hydrateFamily(family.id)
      if (!hydrated) return
      onSelectionChange(hydrated.products.slice(0, maxSelect))
      setActiveFamilyName(hydrated.name)
      onSetNameChange?.(hydrated.name)
      setShowDropdown(false)
      setSearch("")
    } catch (err) {
      console.error("[ProductMultiSelect] hydrateFamily error:", err)
    } finally {
      setPendingFamilyId(null)
    }
  }

  const handleClearAll = () => {
    onSelectionChange([])
    clearFamilyTracking()
  }

  // Filter families by search text (client-side)
  const filteredFamilies = showFamilies
    ? families.filter((f) => {
        if (search.length === 0) return true
        const q = search.toLowerCase()
        return (
          f.name.toLowerCase().includes(q) || f.family_key.toLowerCase().includes(q)
        )
      })
    : []

  const hasFamiliesToShow =
    showDropdown && showFamilies && (filteredFamilies.length > 0 || families.length === 0)

  // Collapse into family badge when many are selected and maxSelect allows grouping
  const useCollapsedView = maxSelect > COLLAPSE_THRESHOLD && selected.length >= COLLAPSE_THRESHOLD

  return (
    <div className="relative">
      {/* Selected tags — individual badges */}
      {selected.length > 0 && !useCollapsedView && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selected.map((p) => (
            <Badge key={p.product_id} variant="secondary" className="text-xs">
              <span className="flex flex-col leading-tight">
                <span>{p.title}</span>
                {p.sku && <span className="text-[10px] text-muted-foreground">{p.sku}</span>}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-transparent"
                onClick={() => handleRemove(p.product_id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Selected tags — collapsed set badge with popover */}
      {useCollapsedView && (
        <div className="flex items-center gap-1 mb-2">
          <Popover>
            <PopoverTrigger asChild>
              <Badge
                variant="secondary"
                className="text-xs cursor-pointer hover:bg-secondary/80"
              >
                <span key={activeFamilyName
                  ? tUi("value0Value1", { value0: String(activeFamilyName), value1: String(selected.length) })
                  : tUi("productSetValue0", { value0: String(selected.length) })}>{activeFamilyName
                  ? tUi("value0Value1", { value0: String(activeFamilyName), value1: String(selected.length) })
                  : tUi("productSetValue0", { value0: String(selected.length) })}</span>
                <ChevronDown className="h-3 w-3 ml-1" />
              </Badge>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-80 p-0 flex flex-col max-h-72"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              {/* Scrollable product list */}
              <div className="overflow-y-auto flex-1 min-h-0">
                <TooltipProvider delayDuration={300}>
                  {selected.map((p) => {
                    const subtitle = productSubtitle(p)
                    return (
                      <Tooltip key={p.product_id}>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-between px-2 py-1.5 hover:bg-accent">
                            <div className="min-w-0 flex-1 mr-2">
                              <p className="truncate text-xs font-medium leading-tight">{p.title}</p>
                              {subtitle && (
                                <p className="truncate text-[10px] leading-tight text-muted-foreground">{subtitle}</p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-0 shrink-0 hover:bg-transparent text-muted-foreground hover:text-foreground"
                              onClick={() => handleRemove(p.product_id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          onPointerDownOutside={(e) => e.preventDefault()}
                        >
                          <p className="text-xs font-medium select-text">{p.title}</p>
                          {subtitle && (
                            <p className="text-[10px] text-muted-foreground select-text">{subtitle}</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
                </TooltipProvider>
              </div>

              {/* Sticky footer — link to centralized rules editor.
                * The picker no longer saves ad-hoc sets; new families are
                * defined on the Product Families page. */}
              {showFamilies && (
                <div className="border-t px-3 py-2 shrink-0">
                  <a
                    href="?page=shopify-bundle-rules"
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    <Settings className="h-3 w-3" />
                    <span><span key={tUi("openProductFamilies")}>{tUi("openProductFamilies")}</span></span>
                  </a>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 shrink-0 text-muted-foreground hover:text-destructive hover:bg-transparent"
            onClick={handleClearAll}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Search input */}
      {canAddMore && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setShowDropdown(true)
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => {
              setTimeout(() => setShowDropdown(false), 200)
            }}
            placeholder={placeholder}
            className="pl-8"
          />
          {isSearching && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      )}

      {/* Dropdown results */}
      {showDropdown && (availableResults.length > 0 || hasFamiliesToShow) && (
        <TooltipProvider delayDuration={300}>
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-80 overflow-y-auto">
          {/* Product families section — read-only group loader (Phase 4c step 2).
            * Save/delete UX moved to ?page=shopify-bundle-rules; one-click load only here. */}
          {hasFamiliesToShow && (
            <>
              <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b sticky top-0 bg-popover flex items-center gap-1.5">
                <Layers className="h-3 w-3" />
                <span><span key={tUi("productFamilies")}>{tUi("productFamilies")}</span></span>
                {isLoadingFamilies && <Loader2 className="h-3 w-3 animate-spin ml-auto" />}
              </div>
              {filteredFamilies.length === 0 && families.length === 0 && !isLoadingFamilies ? (
                <a
                  href="?page=shopify-bundle-rules"
                  className="block w-full text-left px-3 py-2 text-xs text-muted-foreground hover:bg-accent"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span key={tUi("noFamiliesYetOpenProductFamiliesToDefine")}>{tUi("noFamiliesYetOpenProductFamiliesToDefine")}</span></a>
              ) : (
                filteredFamilies.map((family) => {
                  const isPending = pendingFamilyId === family.id
                  return (
                    <div
                      key={family.id}
                      className={cn(
                        "w-full text-left px-2 py-1.5 flex items-center gap-2",
                        isPending
                          ? "opacity-60 cursor-wait"
                          : "hover:bg-accent cursor-pointer",
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (isPending) return
                        void handleSelectFamily(family)
                      }}
                    >
                      <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate flex-1 text-xs font-medium">{family.name}</span>
                      {isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                          {family.product_count}
                        </Badge>
                      )}
                    </div>
                  )
                })
              )}
              {availableResults.length > 0 && <div className="border-b" />}
            </>
          )}

          {/* Select All header */}
          {maxSelect > 1 && availableResults.length > 1 && canAddMore && (
            <div
              role="option"
              aria-selected={false}
              className="w-full text-left px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent border-b flex items-center gap-2 cursor-pointer sticky top-0 bg-popover"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSelectAll}
            >
              <Checkbox
                checked={false}
                className="h-3.5 w-3.5"
                tabIndex={-1}
              />
              <span><span key={tUi("selectAll")}>{tUi("selectAll")}</span></span>{selectAllCount}<span>)</span>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[200px]">
                    <p className="text-xs"><span><span key={tUi("addsUpTo")}>{tUi("addsUpTo")}</span></span> {SELECT_ALL_LIMIT} <span><span key={tUi("productsAtOnce")}>{tUi("productsAtOnce")}</span></span></p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {availableResults.map((product) => {
            const subtitle = productSubtitle(product)
            return (
              <Tooltip key={product.product_id}>
                <TooltipTrigger asChild>
                  <div
                    role="option"
                    aria-selected={false}
                    className="w-full text-left px-2 py-1.5 hover:bg-accent flex items-center gap-2 cursor-pointer"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleToggle(product)}
                  >
                    {maxSelect > 1 && (
                      <Checkbox
                        checked={false}
                        disabled={!canAddMore}
                        className="h-3.5 w-3.5 shrink-0"
                        tabIndex={-1}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium leading-tight">{product.title}</p>
                      {subtitle && (
                        <p className="truncate text-[10px] leading-tight text-muted-foreground">{subtitle}</p>
                      )}
                    </div>
                    <span className="text-[11px] font-semibold tabular-nums text-muted-foreground ml-2 shrink-0">
                      {product.order_count}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  onPointerDownOutside={(e) => e.preventDefault()}
                >
                  <p className="text-xs font-medium select-text">{product.title}</p>
                  {subtitle && (
                    <p className="text-[10px] text-muted-foreground select-text">{subtitle}</p>
                  )}
                </TooltipContent>
              </Tooltip>
            )
          })}

          {/* Limit notice */}
          {!canAddMore && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground text-center border-t">
              <span><span key={tUi("maximum")}>{tUi("maximum")}</span></span> {maxSelect} <span><span key={tUi("productsSelected")}>{tUi("productsSelected")}</span></span>
            </div>
          )}
        </div>
        </TooltipProvider>
      )}

      {showDropdown && search.length >= MIN_SEARCH_LENGTH && !isSearching && results.length === 0 && !hasFamiliesToShow && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md p-3">
          <p className="text-sm text-muted-foreground text-center"><span key={tUi("noProductsFound")}>{tUi("noProductsFound")}</span></p>
        </div>
      )}

      {showDropdown && search.length > 0 && search.length < MIN_SEARCH_LENGTH && !isSearching && !hasFamiliesToShow && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md p-3">
          <p className="text-sm text-muted-foreground text-center">
            <span><span key={tUi("typeAtLeast")}>{tUi("typeAtLeast")}</span></span> {MIN_SEARCH_LENGTH} <span><span key={tUi("charactersToSearch")}>{tUi("charactersToSearch")}</span></span>
          </p>
        </div>
      )}
    </div>
  )
}
